import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAnalyticsEvents as useAnalyticsEventsNext } from '@atlaskit/analytics-next';
import { useSmartLinkContext } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalyticsEvents } from '../../../common/analytics/generated/use-analytics-events';
import { CardDisplay, SmartLinkPosition, SmartLinkSize } from '../../../constants';
import { getDefinitionId, getExtensionKey, getServices } from '../../../state/helpers';
import { useSmartCardState } from '../../../state/store';
import { isSpecialEvent } from '../../../utils';
import { getIsAISummaryEnabled } from '../../../utils/ai-summary';
import { fireLinkClickedEvent } from '../../../utils/analytics/click';
import { flexibleUiOptions } from '../styled';
import { getMetadata } from '../utils';
import ContentContainer from './ContentContainer';
import HoverCardContentOld from './HoverCardContentOld';
import HoverCardForbiddenView from './views/forbidden';
import HoverCardResolvedView from './views/resolved';
import HoverCardLoadingView from './views/resolving';
import HoverCardUnauthorisedView from './views/unauthorised';
export var hoverCardClassName = 'smart-links-hover-preview';
var HoverCardContentNew = function HoverCardContentNew(_ref) {
  var _linkState$status, _cardState$details;
  var _ref$id = _ref.id,
    id = _ref$id === void 0 ? '' : _ref$id,
    cardState = _ref.cardState,
    onActionClick = _ref.onActionClick,
    onResolve = _ref.onResolve,
    renderers = _ref.renderers,
    url = _ref.url,
    onMouseEnter = _ref.onMouseEnter,
    onMouseLeave = _ref.onMouseLeave,
    actionOptions = _ref.actionOptions;
  var _useAnalyticsEventsNe = useAnalyticsEventsNext(),
    createAnalyticsEvent = _useAnalyticsEventsNe.createAnalyticsEvent;
  var _useAnalyticsEvents = useAnalyticsEvents(),
    fireEvent = _useAnalyticsEvents.fireEvent;
  var extensionKey = useMemo(function () {
    return getExtensionKey(cardState.details);
  }, [cardState.details]);
  var linkState = useSmartCardState(url);
  var linkStatus = (_linkState$status = linkState.status) !== null && _linkState$status !== void 0 ? _linkState$status : 'pending';
  var definitionId = useMemo(function () {
    return getDefinitionId(cardState.details);
  }, [cardState.details]);
  var _useSmartLinkContext = useSmartLinkContext(),
    isAdminHubAIEnabled = _useSmartLinkContext.isAdminHubAIEnabled;
  var isAISummaryEnabled = getIsAISummaryEnabled(isAdminHubAIEnabled, cardState.details);
  var services = getServices(linkState.details);
  var statusRef = useRef(linkStatus);
  var fireEventRef = useRef(fireEvent);
  var definitionIdRef = useRef(definitionId);
  useEffect(function () {
    /**
     * Must access object value via ref because its not stable
     * and it can trigger useEffect to re-run below
     */
    if (statusRef.current !== linkStatus) {
      statusRef.current = linkStatus;
    }
    if (fireEventRef.current !== fireEvent) {
      fireEventRef.current = fireEvent;
    }
    if (definitionIdRef.current !== definitionId) {
      definitionIdRef.current = definitionId;
    }
  }, [linkStatus, fireEvent, definitionId]);
  useEffect(function () {
    var _definitionIdRef$curr;
    var previewDisplay = 'card';
    var previewInvokeMethod = 'mouse_hover';
    var cardOpenTime = Date.now();
    var fireEventCurrent = fireEventRef.current;
    fireEventCurrent('ui.hoverCard.viewed', {
      previewDisplay: previewDisplay,
      previewInvokeMethod: previewInvokeMethod,
      definitionId: (_definitionIdRef$curr = definitionIdRef.current) !== null && _definitionIdRef$curr !== void 0 ? _definitionIdRef$curr : null
    });
    return function () {
      var _definitionIdRef$curr2;
      var hoverTime = Date.now() - cardOpenTime;
      fireEventCurrent('ui.hoverCard.dismissed', {
        previewDisplay: previewDisplay,
        previewInvokeMethod: previewInvokeMethod,
        hoverTime: hoverTime,
        definitionId: (_definitionIdRef$curr2 = definitionIdRef.current) !== null && _definitionIdRef$curr2 !== void 0 ? _definitionIdRef$curr2 : null
      });
    };
  }, []);
  var onClick = useCallback(function (event) {
    var isModifierKeyPressed = isSpecialEvent(event);
    fireEvent('ui.smartLink.clicked.titleGoToLink', {
      id: id,
      display: CardDisplay.HoverCardPreview,
      isModifierKeyPressed: isModifierKeyPressed,
      definitionId: definitionId !== null && definitionId !== void 0 ? definitionId : null
    });
    fireLinkClickedEvent(createAnalyticsEvent)(event);
  }, [createAnalyticsEvent, id, fireEvent, definitionId]);
  var data = (_cardState$details = cardState.details) === null || _cardState$details === void 0 ? void 0 : _cardState$details.data;
  var _getMetadata = getMetadata(extensionKey, data),
    subtitle = _getMetadata.subtitle;
  var titleMaxLines = subtitle && subtitle.length > 0 ? 1 : 2;
  var titleBlockProps = {
    maxLines: titleMaxLines,
    size: SmartLinkSize.Large,
    position: SmartLinkPosition.Center,
    subtitle: subtitle
  };
  var flexibleCardProps = {
    appearance: CardDisplay.HoverCardPreview,
    cardState: cardState,
    onClick: onClick,
    onResolve: onResolve,
    origin: 'smartLinkPreviewHoverCard',
    renderers: renderers,
    actionOptions: actionOptions,
    ui: flexibleUiOptions,
    url: url,
    children: null
  };
  var onClickStopPropagation = useCallback(function (e) {
    return e.stopPropagation();
  }, []);
  var getCardView = function getCardView(cardState) {
    if (cardState.metadataStatus === 'pending') {
      return /*#__PURE__*/React.createElement(HoverCardLoadingView, {
        flexibleCardProps: flexibleCardProps,
        titleBlockProps: titleBlockProps
      });
    }
    if (cardState.status === 'unauthorized' && services !== null && services !== void 0 && services.length) {
      return /*#__PURE__*/React.createElement(HoverCardUnauthorisedView, {
        extensionKey: extensionKey,
        id: id,
        flexibleCardProps: flexibleCardProps,
        url: url
      });
    }
    if (cardState.status === 'forbidden' || cardState.status === 'not_found') {
      return /*#__PURE__*/React.createElement(HoverCardForbiddenView, {
        flexibleCardProps: flexibleCardProps
      });
    }
    if (cardState.status === 'resolved') {
      return /*#__PURE__*/React.createElement(HoverCardResolvedView, {
        cardState: cardState,
        extensionKey: extensionKey,
        flexibleCardProps: flexibleCardProps,
        isAISummaryEnabled: isAISummaryEnabled,
        onActionClick: onActionClick,
        titleBlockProps: titleBlockProps,
        url: url
      });
    }
    return null;
  };
  var cardView = getCardView(cardState);
  return cardView ? /*#__PURE__*/React.createElement(ContentContainer, {
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    onClick: onClickStopPropagation,
    isAIEnabled: isAISummaryEnabled,
    url: url
  }, cardView) : null;
};
var HoverCardContent = function HoverCardContent(props) {
  if (fg('bandicoots-compiled-migration-smartcard')) {
    return /*#__PURE__*/React.createElement(HoverCardContentNew, props);
  } else {
    return /*#__PURE__*/React.createElement(HoverCardContentOld, props);
  }
};
export default HoverCardContent;